import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { 
  PlusIcon,
  CubeIcon,
  TrashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  CalendarIcon,
  TagIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const ClientProductsTab = ({ client }) => {
  const [clientProducts, setClientProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    if (client?.id) {
      fetchClientProducts();
      fetchAvailableProducts();
    }
  }, [client?.id]);

  const fetchClientProducts = async () => {
    try {
      const response = await api.get(`/products/client/${client.id}`);
      setClientProducts(response.data);
    } catch (err) {
      console.error('Error fetching client products:', err);
      setError('Failed to load client products');
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const response = await api.get(`/products/client/${client.id}/available`);
      setAvailableProducts(response.data);
    } catch (err) {
      console.error('Error fetching available products:', err);
      setError('Failed to load available products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      setError('Please select a product');
      return;
    }

    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    try {
      setAddLoading(true);
      setError(null);

      await api.post(`/products/client/${client.id}/products`, {
        product_id: parseInt(selectedProduct),
        start_date: startDate,
        notes: notes.trim() || null
      });

      setSuccess('Product added successfully');
      setShowAddModal(false);
      setSelectedProduct('');
      setStartDate('');
      setEndDate('');
      setNotes('');
      
      // Refresh both lists
      await fetchClientProducts();
      await fetchAvailableProducts();

    } catch (err) {
      console.error('Error adding product:', err);
      setError(err.response?.data?.error || 'Failed to add product');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    if (endDate && new Date(endDate) < new Date(startDate)) {
      setError('End date cannot be before start date');
      return;
    }

    try {
      setEditLoading(true);
      setError(null);

      await api.put(`/products/client-products/${editingProduct.id}`, {
        start_date: startDate,
        end_date: endDate || null,
        notes: notes.trim() || null
      });

      setSuccess('Product updated successfully');
      setShowEditModal(false);
      setEditingProduct(null);
      setStartDate('');
      setEndDate('');
      setNotes('');
      
      // Refresh both lists
      await fetchClientProducts();
      await fetchAvailableProducts();

    } catch (err) {
      console.error('Error updating product:', err);
      setError(err.response?.data?.error || 'Failed to update product');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRemoveProduct = async (clientProductId, productName) => {
    if (!window.confirm(`Are you sure you want to remove "${productName}" from this client?`)) {
      return;
    }

    try {
      setRemoveLoading(clientProductId);
      setError(null);

      await api.delete(`/products/client-products/${clientProductId}`);
      
      setSuccess('Product removed successfully');
      
      // Refresh both lists
      await fetchClientProducts();
      await fetchAvailableProducts();

    } catch (err) {
      console.error('Error removing product:', err);
      setError(err.response?.data?.error || 'Failed to remove product');
    } finally {
      setRemoveLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Convert to YYYY-MM-DD format for HTML date input
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    if (!endDate) {
      return `Started: ${start} (Active)`;
    }
    const end = formatDate(endDate);
    return `${start} - ${end}`;
  };

  const isProductActive = (endDate) => {
    return !endDate || new Date(endDate) > new Date();
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const openEditModal = (clientProduct) => {
    console.log('Opening edit modal with product:', clientProduct); // Debug log
    setEditingProduct(clientProduct);
    setStartDate(formatDateForInput(clientProduct.start_date));
    setEndDate(formatDateForInput(clientProduct.end_date));
    setNotes(clientProduct.notes || '');
    clearMessages();
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {/* Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6 border-l-4 border-red-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4 mb-6 border-l-4 border-green-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">{success}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Header - Enhanced with Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-indigo-900">Products</h3>
              <p className="mt-1 text-sm text-indigo-700">
                Manage products assigned to {client.name}
              </p>
            </div>
            <button
              onClick={() => {
                clearMessages();
                setShowAddModal(true);
              }}
              disabled={availableProducts.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Product
            </button>
          </div>
        </div>
      </div>

      {/* Current Products */}
      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        {clientProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
              <CubeIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <h3 className="mt-4 text-sm font-medium text-gray-900">No products assigned</h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by assigning a product to this client.
            </p>
            {availableProducts.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => {
                    clearMessages();
                    setShowAddModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add First Product
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {clientProducts.map((clientProduct) => (
              <li 
                key={clientProduct.id} 
                className={`px-6 py-4 ${!isProductActive(clientProduct.end_date) ? 'bg-gray-50 opacity-75' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <CubeIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      {/* Product Information - Neutral Theme */}
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {clientProduct.product_name}
                        </div>
                        {!isProductActive(clientProduct.end_date) && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Ended
                          </span>
                        )}
                      </div>

                      {/* Category & Dates - Subtle Gray */}
                      <div className="flex items-center mt-1 space-x-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <TagIcon className="h-4 w-4 mr-1" />
                          {clientProduct.category_display_label}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDateRange(clientProduct.start_date, clientProduct.end_date)}
                        </div>
                      </div>

                      {/* Description & Notes - Muted Gray */}
                      {clientProduct.product_description && (
                        <div className="text-sm text-gray-500 mt-1">
                          {clientProduct.product_description}
                        </div>
                      )}
                      {clientProduct.notes && (
                        <div className="text-sm text-gray-500 mt-1 italic">
                          Note: {clientProduct.notes}
                        </div>
                      )}

                      {/* Price Book Link - Subtle Emphasis */}
                      {clientProduct.price_book_item_name && (
                        <div className="text-sm text-gray-700 mt-1 font-medium">
                          Linked to: {clientProduct.price_book_item_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(clientProduct)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                      title="Edit product assignment"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveProduct(clientProduct.id, clientProduct.product_name)}
                      disabled={removeLoading === clientProduct.id}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Remove product"
                    >
                      {removeLoading === clientProduct.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500"></div>
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Product Modal - Indigo Theme */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="bg-indigo-50 px-4 py-3 rounded-t-md border-b border-indigo-100">
                <h3 className="text-lg font-medium text-indigo-900">
                  Add Product to {client.name}
                </h3>
              </div>
              
              {availableProducts.length === 0 ? (
                <div className="text-center py-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
                    <CubeIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    No products available to assign. All active products are already assigned to this client.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleAddProduct} className="space-y-4 p-4">
                  {/* Product Selection */}
                  <div>
                    <label htmlFor="product" className="block text-sm font-medium text-gray-700">
                      Product *
                    </label>
                    <select
                      id="product"
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    >
                      <option value="">Select a product...</option>
                      {availableProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.product_name} ({product.category_display_label})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Optional notes about this product assignment..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setSelectedProduct('');
                        setStartDate('');
                        setEndDate('');
                        setNotes('');
                        clearMessages();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={addLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {addLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        'Add Product'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal - Blue Theme */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="bg-blue-50 px-4 py-3 rounded-t-md border-b border-blue-100">
                <h3 className="text-lg font-medium text-blue-900">
                  Edit Product Assignment
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Editing: <strong>{editingProduct.product_name}</strong> for {client.name}
                </p>
              </div>
              
              <form onSubmit={handleEditProduct} className="space-y-4 p-4">
                {/* Start Date */}
                <div>
                  <label htmlFor="editStartDate" className="block text-sm font-medium text-gray-700">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="editStartDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="editEndDate" className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="editEndDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Leave empty if the product is still active
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="editNotes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    id="editNotes"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Optional notes about this product assignment..."
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingProduct(null);
                      setStartDate('');
                      setEndDate('');
                      setNotes('');
                      clearMessages();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {editLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      'Update Product'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProductsTab; 